import { installedApp } from '../native/appInfo';
import { isNative } from '../native/platform';
import { useSession } from '../store/session';
import { useUpdate } from './store';

const HOUR = 60 * 60_000;
/** Resumes come in bursts; the server is not asked about builds more often than this. */
const MIN_CHECK_GAP = 5 * 60_000;

let registration: ServiceWorkerRegistration | null = null;
let lastCheck = 0;

/**
 * Looks for a newer build: the Android shell asks the server which APK is published, the
 * web app asks the browser to re-fetch the service worker. Cheap enough to run on every
 * resume, throttled so a burst of resumes costs one request.
 */
export function checkForUpdates(force = false): void {
  const now = Date.now();
  if (!force && now - lastCheck < MIN_CHECK_GAP) return;
  lastCheck = now;
  if (isNative) {
    void useSession.getState().refreshMeta();
  } else {
    void registration?.update().catch(() => undefined);
  }
}

/** Starts the watch once at boot: the first check, hourly checks and a check on every resume. */
export function startUpdateWatch(): void {
  if (isNative) {
    void installedApp().then((info) => useUpdate.getState().setInstalled(info));
  } else if ('serviceWorker' in navigator) {
    void import('virtual:pwa-register').then(({ registerSW }) => {
      const update = registerSW({
        immediate: true,
        onNeedRefresh() {
          useUpdate.getState().setWebReady(() => update(true));
          // The banner names the build waiting; the session's copy of the server meta may still
          // predate the deploy that produced it.
          void useSession.getState().refreshMeta();
        },
        onRegisteredSW(_url, r) {
          registration = r ?? null;
        },
      });
    });
    // The server reports its build on every meta refresh; a mismatch means a service worker
    // update is waiting to be found, so look for it right away instead of at the next hour.
    useSession.subscribe((s, prev) => {
      if (s.meta && s.meta !== prev.meta && s.meta.buildId !== __BUILD_ID__) checkForUpdates(true);
    });
  }
  lastCheck = Date.now();
  setInterval(() => checkForUpdates(true), HOUR);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdates();
  });
}
